import { useState } from "react";
import type { Bootstrap } from "../../shared/types";
import { api, type IncomeInput } from "../lib/api";
import { parseMoney } from "../lib/format";
import Modal from "./Modal";

// Edit monthly income + pay day per household member.
export default function IncomeModal({
  data,
  onClose,
  onSaved,
}: {
  data: Bootstrap;
  onClose: () => void;
  onSaved: () => void;
}) {
  const initial = data.household.members.map((m) => {
    const entry = data.income.find((i) => i.memberId === m.id);
    return {
      memberId: m.id,
      name: m.name,
      amount: entry ? (entry.amountMinor / 100).toFixed(2) : "",
      payDay: entry?.payDay != null ? String(entry.payDay) : "",
    };
  });
  const [rows, setRows] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  function update(id: number, field: "amount" | "payDay", value: string) {
    setRows((rs) => rs.map((r) => (r.memberId === id ? { ...r, [field]: value } : r)));
  }

  async function save() {
    const entries: IncomeInput[] = [];
    for (const r of rows) {
      const minor = parseMoney(r.amount) ?? 0;
      const payDay = r.payDay ? Number(r.payDay) : null;
      if (payDay != null && (payDay < 1 || payDay > 31)) return setError("Pay day must be 1–31");
      entries.push({ memberId: r.memberId, amountMinor: minor, payDay });
    }
    setSaving(true);
    try {
      await api.saveIncome(entries);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Modal title="Income" onClose={onClose}>
      <div className="form">
        <p className="page__lede" style={{ margin: 0 }}>Salary amounts and the day each lands.</p>
        {rows.map((r) => (
          <div className="form__row" key={r.memberId}>
            <label className="field">
              <span className="field__label">{r.name} — monthly (£)</span>
              <input className="input" inputMode="decimal" value={r.amount} onChange={(e) => update(r.memberId, "amount", e.target.value)} placeholder="3293.38" />
            </label>
            <label className="field" style={{ maxWidth: 110 }}>
              <span className="field__label">Pay day</span>
              <input className="input" inputMode="numeric" value={r.payDay} onChange={(e) => update(r.memberId, "payDay", e.target.value)} placeholder="25" />
            </label>
          </div>
        ))}
        {error && <p className="form__error">{error}</p>}
        <div className="form__actions">
          <button className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save income"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
